import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminProfile } from '../api/client';
import type { TenantPage } from '../api/types';
import { useApi } from '../lib/use-api';

export interface TenantSwitcherProps {
  isSystemAdmin: boolean;
  selectedTenant?: string | undefined;
  profile?: AdminProfile | undefined;
  onTenantChange: (tenantId: string | undefined) => void;
}

/**
 * Hiển thị và chuyển đổi đơn vị (Tenant) đang thao tác trên workspace.
 *
 * - Quản trị viên hệ thống (system_admin): có thể chuyển đổi trực tiếp giữa các đơn vị
 *   qua dropdown selector hoặc quay về toàn hệ thống (mặc định).
 * - Các vai trò trực thuộc: hiển thị tên đơn vị trực thuộc (read-only).
 */
export function TenantSwitcher({
  isSystemAdmin,
  selectedTenant,
  profile,
  onTenantChange,
}: TenantSwitcherProps): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const tenants = useApi<TenantPage>('/api/admin/tenants', {}, isSystemAdmin);

  if (!isSystemAdmin) {
    const displayName = profile?.tenantName || profile?.tenantId;
    if (!displayName) return null;

    return (
      <div
        className="tenant-badge"
        title={`${t('admin:tenants.affiliatedTenant')}: ${displayName} (${profile?.tenantId ?? ''})`}
      >
        <span className="tenant-badge__icon" aria-hidden="true">
          🏢
        </span>
        <span className="tenant-badge__text">
          <strong className="tenant-badge__name">{displayName}</strong>
        </span>
      </div>
    );
  }

  const items = tenants.data?.items ?? [];
  const selectedItem = items.find((item) => item.id === selectedTenant);

  return (
    <div className={`tenant-switcher ${selectedTenant ? 'tenant-switcher--active' : ''}`}>
      <span className="tenant-switcher__icon" aria-hidden="true">
        🏢
      </span>
      <label className="tenant-switcher__label" htmlFor="workspace-tenant-select">
        <span className="visually-hidden">{t('admin:tenants.switchLabel')}</span>
        <select
          id="workspace-tenant-select"
          className="tenant-switcher__select"
          value={selectedTenant ?? ''}
          onChange={(e) => onTenantChange(e.target.value ? e.target.value : undefined)}
          disabled={tenants.loading && items.length === 0}
          title={selectedItem ? `${selectedItem.displayName} (${selectedItem.slug})` : t('admin:tenants.allTenants')}
        >
          <option value="">{t('admin:tenants.allTenants')}</option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.displayName} ({item.slug})
              {item.status === 'suspended' ? ` [${t('admin:tenants.suspendedTag')}]` : ''}
            </option>
          ))}
        </select>
      </label>

      {selectedTenant ? (
        <button
          type="button"
          className="tenant-switcher__clear"
          title={t('admin:tenants.clearTenantFilter')}
          aria-label={t('admin:tenants.clearTenantFilter')}
          onClick={() => onTenantChange(undefined)}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}
